/**
 * データの取込（#268 1.5c-1、docs/98 §6・docs/68 §1）：未入金の請求書と取引先（#288、docs/101 §3）。PC だけ（PC_ONLY_FEATURES の dataImport）・管理者だけ。
 *   /imports/new?kind=partners で取引先（列の対応に「仕入先コードの接頭辞」。確認の行は 新規／更新（相手の名前））
 *   /imports/new   取込元とファイル
 *   /imports/:id   読み込みの確認と列の対応（checking）→ 確認＝試し取込（ready）→ 完了（imported・取消できる）／取消済
 * 行の確かめ・件数は API（contract の imports.ts）。画面は答えを見せて、取り込まない・{customer}を登録 を送るだけ
 */
import {
  IMPORT_ENCODING_LABEL,
  IMPORT_ISSUE_MSG,
  IMPORT_ISSUE_SEVERITY,
  IMPORT_MSG as M,
  IMPORT_SOURCE_LABEL,
  PARTNER_FIELDS,
  PARTNER_PICKER_MSG,
  UNPAID_FIELDS,
  encodeTitleHeader,
  importRowState,
  type ImportCounts,
  type ImportDetail,
  type ImportEncoding,
  type ImportMapping,
  type ImportPreview,
  type ImportRowView,
  type ImportSource,
  type ImportKind,
  type ImportOptions,
  type ImportUndoResult,
  type PartnerRowValue,
  type UnpaidRowValue,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, apiUpload, messageOf } from "../../api.js";
import { BackButton, usePageName } from "../../components/BackButton.jsx";
import { FileDrop } from "../../components/FileDrop.jsx";
import { ListHead } from "../../components/ListHead.jsx";
import { ListTabs, listTabPanel } from "../../components/ListTabs.jsx";
import { Segmented } from "../../components/Segmented.jsx";
import { ErrorText, Help, useToast } from "../../components/ui.jsx";
import { t } from "../../labels.js";
import { md, yen } from "../../lib/num.js";

const BACK = { to: "/settings/imports", label: M.title };
const STEPS = [M.stepFile, M.stepRead, M.stepMap, M.stepCheck, M.stepDone] as const;

function Steps({ at }: { at: number }) {
  return (
    <ol className="imp-steps" aria-label="取込の段" data-testid="import-steps">
      {STEPS.map((s, i) => (
        <li key={s} className={i === at ? "is-on" : i < at ? "is-done" : undefined} aria-current={i === at ? "step" : undefined}>
          {s}
        </li>
      ))}
    </ol>
  );
}

const titleOf = (kind: ImportKind) => (kind === "partners" ? M.newPartners : M.newUnpaid);
const FIELDS_OF = (kind: ImportKind): readonly { key: string; label: string; required: boolean }[] => (kind === "partners" ? PARTNER_FIELDS : UNPAID_FIELDS);

/** 取込元とファイル */
export function ImportNewPage() {
  const [sp] = useSearchParams();
  const kind: ImportKind = sp.get("kind") === "partners" ? "partners" : "unpaid_invoices";
  usePageName(titleOf(kind));
  const nav = useNavigate();
  const [source, setSource] = useState<ImportSource>("board");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const onFiles = async (files: File[]) => {
    const f = files[0];
    if (!f || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const p = await apiUpload<ImportPreview>(`/imports?kind=${kind}&source=${source}`, f, f.type || "text/csv", { "x-gb-title": encodeTitleHeader(f.name) });
      nav(`/imports/${p.id}`, { replace: true });
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };
  return (
    <ListHead back={<BackButton fallback={BACK} />} title={<>{titleOf(kind)}<Help testId="help-import" label={M.title} text={M.help} /></>} titleTestId="import-title">
      <Steps at={0} />
      <div className="lcard imp-card">
        <div className="imp-row">
          <span className="imp-lbl" id="impSource">{M.source}</span>
          <Segmented<ImportSource> labelledBy="impSource" options={(["board", "generic"] as const).map((v) => ({ value: v, label: IMPORT_SOURCE_LABEL[v], testId: `import-source-${v}` }))} value={source} onChange={setSource} />
        </div>
        <FileDrop onFiles={(fs) => void onFiles(fs)} accept=".csv,.tsv,.txt,text/csv,text/plain,text/tab-separated-values" phoneAccept=".csv,.tsv,.txt" multiple={false} hint={M.fileHelp} disabled={busy} testId="import-drop" inputTestId="import-file" />
        <p className="hint">{M.unchanged}</p>
        <ErrorText>{err ?? undefined}</ErrorText>
      </div>
    </ListHead>
  );
}

const ISSUE_FIELD_LABEL = Object.fromEntries([...UNPAID_FIELDS, ...PARTNER_FIELDS].map((f) => [f.key, f.label])) as Record<string, string>;
const issueText = (x: ImportRowView["issues"][number]) => `${x.field ? `${t(ISSUE_FIELD_LABEL[x.field] ?? "")}：` : ""}${t(IMPORT_ISSUE_MSG[x.code])}`;
type Tab = "all" | "ok" | "confirm" | "error" | "skip";
const TAB_LABEL: Record<Tab, string> = { all: M.filterAll, ok: M.filterOk, confirm: M.filterConfirm, error: M.filterError, skip: M.filterSkip };
const STATE_CHIP: Record<ImportRowView["state"], { cls: string; label: string }> = {
  ok: { cls: "chip ai", label: M.filterOk },
  confirm: { cls: "chip shu", label: M.filterConfirm },
  error: { cls: "chip shu", label: M.filterError },
  skip: { cls: "chip quiet", label: M.filterSkip },
};

/** 読み込みの確認・列の対応・確認・完了 */
export function ImportPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const q = useQuery({ queryKey: ["import", id], queryFn: () => api<ImportDetail>("GET", `/imports/${id}`) });
  const [remap, setRemap] = useState(false);
  const d = q.data;
  const mapping = !!d && (d.status === "checking" || remap);
  const pv = useQuery({ queryKey: ["import-preview", id], queryFn: () => api<ImportPreview>("GET", `/imports/${id}/preview`), enabled: mapping });
  const set = (x: ImportDetail) => qc.setQueryData(["import", id], x);
  usePageName(titleOf(d?.kind ?? "unpaid_invoices"));
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!d) return <p className="hint">読み込んでいます…</p>;
  const step = mapping ? 2 : d.status === "ready" ? 3 : 4;
  return (
    <ListHead back={<BackButton fallback={BACK} />} title={<>{titleOf(d.kind)}<Help testId="help-import" label={M.title} text={M.help} /></>} titleTestId="import-title">
      <Steps at={step} />
      <p className="imp-file" data-testid="import-file-name">
        {d.fileName}
        <span className="muted">　{IMPORT_SOURCE_LABEL[d.source]}</span>
      </p>
      {mapping ? (
        pv.data ? (
          <MappingStep key={`${pv.data.encoding}|${JSON.stringify(pv.data.mapping)}`} pv={pv.data} onPreview={(p) => qc.setQueryData(["import-preview", id], p)} onChecked={(x) => (set(x), setRemap(false))} />
        ) : pv.isError ? (
          <ErrorText>{messageOf(pv.error)}</ErrorText>
        ) : (
          <p className="hint">読み込んでいます…</p>
        )
      ) : d.status === "ready" ? (
        <CheckStep d={d} onChange={set} onRemap={() => setRemap(true)} toast={toast} />
      ) : (
        <DoneStep d={d} onUndone={() => void q.refetch()} toast={toast} />
      )}
      {node}
    </ListHead>
  );
}

function MappingStep({ pv, onPreview, onChecked }: { pv: ImportPreview; onPreview: (p: ImportPreview) => void; onChecked: (d: ImportDetail) => void }) {
  const [map, setMap] = useState<ImportMapping>(pv.mapping);
  const [options, setOptions] = useState<ImportOptions>(pv.options);
  const fields = FIELDS_OF(pv.kind);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reread = async (encoding: ImportEncoding) => {
    setBusy(true);
    setErr(null);
    try {
      onPreview(await api<ImportPreview>("PUT", `/imports/${pv.id}/read`, { encoding }));
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const check = async () => {
    setBusy(true);
    setErr(null);
    try {
      onChecked(await api<ImportDetail>("PUT", `/imports/${pv.id}/mapping`, pv.kind === "partners" ? { mapping: map, options } : { mapping: map }));
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };
  const used = new Set(Object.values(map).filter((x): x is number => x != null));
  const unmapped = pv.headers.filter((_, i) => !used.has(i));
  const other: ImportEncoding = pv.encoding === "utf8" ? "cp932" : "utf8";
  return (
    <>
      <div className="lcard imp-card" data-testid="import-read">
        <div className="imp-row">
          <span className="imp-lbl">{M.encoding}</span>
          <b data-testid="import-encoding">{IMPORT_ENCODING_LABEL[pv.encoding]}</b>
          <span className="muted">{M.rows(pv.rowCount)}</span>
          <span className="sp" />
          <button type="button" className="btn sm" disabled={busy} onClick={() => void reread(other)} data-testid="import-reread">
            {M.rereadOther(IMPORT_ENCODING_LABEL[other])}
          </button>
        </div>
        <div className="tbl-wrap">
          <table className="list imp-sample" data-testid="import-sample">
            <thead>
              <tr>
                {pv.headers.map((h, i) => (
                  <th key={i}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pv.sample.map((r, i) => (
                <tr key={i}>
                  {pv.headers.map((_, k) => (
                    <td key={k}>{r[k] ?? ""}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="hint">{M.readOk}</p>
      </div>
      <div className="lcard imp-card" data-testid="import-mapping">
        <h2 className="imp-h">{M.stepMap}</h2>
        <div className="imp-map">
          {fields.map((f) => (
            <label key={f.key} className="imp-mrow">
              <span className="imp-lbl">
                {t(f.label)}
                {f.required ? <span className="imp-req">必須</span> : null}
              </span>
              <span className="mselw">
                <select
                  className="inp"
                  value={map[f.key] == null ? "" : String(map[f.key])}
                  onChange={(e) => setMap((m) => ({ ...m, [f.key]: e.target.value === "" ? null : Number(e.target.value) }))}
                  data-testid={`import-map-${f.key}`}
                >
                  <option value="">{M.noColumn}</option>
                  {pv.headers.map((h, i) => (
                    <option key={i} value={i}>
                      {h || `（${i + 1}列目）`}
                    </option>
                  ))}
                </select>
              </span>
            </label>
          ))}
        </div>
        {pv.kind === "partners" ? (
          <label className="imp-mrow">
            <span className="imp-lbl">{PARTNER_PICKER_MSG.vendorCodePrefix}</span>
            <input className="inp code" value={options.vendorCodePrefix} maxLength={5} placeholder="S-" onChange={(e) => setOptions({ vendorCodePrefix: e.target.value })} data-testid="import-vendor-prefix" />
          </label>
        ) : null}
        {unmapped.length ? (
          <p className="hint" data-testid="import-unmapped">
            {M.unmapped}：{unmapped.join("、")}
          </p>
        ) : null}
        <ErrorText>{err ?? undefined}</ErrorText>
        <div className="imp-acts">
          <button type="button" className="btn primary" disabled={busy} onClick={() => void check()} data-testid="import-check">
            {M.check}
          </button>
        </div>
      </div>
    </>
  );
}

function CheckStep({ d, onChange, onRemap, toast }: { d: ImportDetail; onChange: (d: ImportDetail) => void; onRemap: () => void; toast: ReturnType<typeof useToast>["toast"] }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("all");
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const rows = useMemo(() => (tab === "all" ? d.rows : d.rows.filter((r) => r.state === tab)), [d.rows, tab]);
  const counts = d.counts;
  const update = async (r: ImportRowView, body: { action?: "new" | "skip"; createClient?: boolean }) => {
    // 押した瞬間に行を変えて見せる（件数は答えで）
    const action = body.action ?? r.action;
    const createClient = body.createClient ?? r.createClient;
    onChange({ ...d, rows: d.rows.map((o) => (o.id === r.id ? { ...o, action, createClient, state: importRowState(o.issues, action, createClient) } : o)) });
    try {
      const x = await api<{ row: ImportRowView; counts: ImportCounts }>("PUT", `/imports/${d.id}/rows/${r.id}`, body);
      onChange({ ...d, rows: d.rows.map((o) => (o.id === x.row.id ? x.row : o)), counts: x.counts });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const x = await api<ImportDetail>("POST", `/imports/${d.id}/commit`);
      setAsk(false);
      onChange(x);
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      void qc.invalidateQueries({ queryKey: ["partners"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      void qc.invalidateQueries({ queryKey: ["imports"] });
    } catch (e) {
      setAsk(false);
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };
  const blocked = counts.error > 0 || counts.confirm > 0 || counts.ok === 0;
  const partners = d.kind === "partners";
  const TYPE_LABEL: Record<PartnerRowValue["partnerType"], string> = { client: t("{customer}"), vendor: t("{vendor}"), both: "両方" };
  const tabs = (["all", "ok", "confirm", "error", "skip"] as const).map((k) => ({ key: k, label: TAB_LABEL[k], count: k === "all" ? counts.total : counts[k] }));
  return (
    <>
      <div className="imp-sum" data-testid="import-counts">
        <b>{M.countsLine(counts.ok, counts.confirm, counts.error, counts.skip)}</b>
        {partners ? null : (
          <span className="muted">
            {M.amount} <b className="num">{yen(counts.amount)}</b> 円
          </span>
        )}
        <span className="sp" />
        <button type="button" className="btn" onClick={onRemap} data-testid="import-remap">
          {M.remap}
        </button>
        <button type="button" className="btn primary" disabled={blocked || busy} title={blocked && counts.ok > 0 ? M.commitBlocked : undefined} onClick={() => setAsk(true)} data-testid="import-commit">
          {M.commit}
        </button>
      </div>
      <div className="lcard">
        <ListTabs id="imp" label="取込の行を状態で絞る" tabs={tabs} value={tab} onChange={(k) => setTab(k as Tab)} />
        <div className="tbl-wrap ltab-panel" {...listTabPanel("imp", tab)}>
          <table className="list imp-rows" data-testid="import-rows">
            <thead>
              {partners ? (
                <tr>
                  <th className="num">{M.colRow}</th>
                  <th>{M.colState}</th>
                  <th>{ISSUE_FIELD_LABEL.name}</th>
                  <th>{ISSUE_FIELD_LABEL.partnerType}</th>
                  <th>{ISSUE_FIELD_LABEL.customerCode}</th>
                  <th>{ISSUE_FIELD_LABEL.vendorCode}</th>
                  <th>{ISSUE_FIELD_LABEL.branchName}</th>
                  <th>{M.colPartnerAction}</th>
                  <th />
                </tr>
              ) : (
                <tr>
                  <th className="num">{M.colRow}</th>
                  <th>{M.colState}</th>
                  <th>{t(ISSUE_FIELD_LABEL.managementNo!)}</th>
                  <th>{t(ISSUE_FIELD_LABEL.clientName!)}</th>
                  <th>{t(ISSUE_FIELD_LABEL.subject!)}</th>
                  <th>{t(ISSUE_FIELD_LABEL.issueDate!)}</th>
                  <th>{t(ISSUE_FIELD_LABEL.dueDate!)}</th>
                  <th className="num">{t(ISSUE_FIELD_LABEL.total!)}</th>
                  <th />
                </tr>
              )}
            </thead>
            <tbody>
              {rows.map((r) => {
                const chip = STATE_CHIP[r.state];
                const needClient = r.issues.some((x) => x.code === "client_not_found");
                const hasError = r.issues.some((x) => IMPORT_ISSUE_SEVERITY[x.code] === "error");
                return (
                  <tr key={r.id} data-testid={`import-row-${r.rowNo}`}>
                    <td className="num">{r.rowNo}</td>
                    <td>
                      <span className={chip.cls} data-testid={`import-row-state-${r.rowNo}`}>{chip.label}</span>
                    </td>
                    {partners ? (
                      <PartnerCells r={r} typeLabel={TYPE_LABEL} />
                    ) : (
                      <UnpaidCells v={r.value as UnpaidRowValue} />
                    )}
                    <td className="imp-ops">
                      {r.issues.length ? (
                        <ul className="imp-issues">
                          {r.issues.map((x, i) => (
                            <li key={i} className={IMPORT_ISSUE_SEVERITY[x.code] === "warn" ? "muted" : "shu"}>
                              {issueText(x)}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      <span className="imp-btns">
                        {needClient && r.action === "new" && !hasError ? (
                          <label className="imp-check">
                            <input type="checkbox" checked={r.createClient} onChange={(e) => void update(r, { createClient: e.target.checked })} data-testid={`import-row-client-${r.rowNo}`} />
                            {t(M.createClient("{customer}"))}
                          </label>
                        ) : null}
                        <button type="button" className="btn text sm" onClick={() => void update(r, { action: r.action === "skip" ? "new" : "skip" })} data-testid={`import-row-skip-${r.rowNo}`}>
                          {r.action === "skip" ? M.unskip : M.skip}
                        </button>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {ask ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && setAsk(false)}>
          <div className="dlg" role="dialog" aria-label={M.commit} onKeyDown={(e) => e.key === "Escape" && !busy && setAsk(false)}>
            <div className="dh">{M.commit}</div>
            <div className="db">
              <p data-testid="import-commit-text">{partners ? M.commitConfirmPartners(counts.newPartners, counts.updatePartners) : t(M.commitConfirm(counts.ok, counts.newClients))}</p>
            </div>
            <div className="df">
              <button type="button" className="btn" disabled={busy} onClick={() => setAsk(false)}>
                取消
              </button>
              <button type="button" className="btn primary" autoFocus disabled={busy} onClick={() => void commit()} data-testid="import-commit-confirm">
                決定
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function DoneStep({ d, onUndone, toast }: { d: ImportDetail; onUndone: () => void; toast: ReturnType<typeof useToast>["toast"] }) {
  const qc = useQueryClient();
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const undoable = d.status === "imported" && !!d.undoUntil && new Date(d.undoUntil).getTime() > Date.now();
  const until = d.undoUntil ? md(new Date(new Date(d.undoUntil).getTime() + 9 * 3600 * 1000).toISOString().slice(0, 10)) : "";
  const undo = async () => {
    setBusy(true);
    try {
      const r = await api<ImportUndoResult>("POST", `/imports/${d.id}/undo`);
      setAsk(false);
      toast(d.kind === "partners" ? M.undonePartners(r.deleted, r.kept, r.restored) : M.undone(r.deleted, r.kept), undefined, "success", "import-undone");
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      void qc.invalidateQueries({ queryKey: ["partners"] });
      void qc.invalidateQueries({ queryKey: ["imports"] });
      onUndone();
    } catch (e) {
      setAsk(false);
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="lcard imp-card" data-testid="import-done">
      {d.status === "undone" ? (
        <p data-testid="import-done-text">
          <span className="chip quiet">取消済</span> {M.undone(d.result?.undoDeleted ?? 0, d.result?.undoKept ?? 0)}
        </p>
      ) : (
        <p data-testid="import-done-text">{d.kind === "partners" ? M.donePartners(d.result?.created ?? 0, d.result?.updated ?? 0, until) : M.done(d.result?.imported ?? 0, until)}</p>
      )}
      <div className="imp-acts">
        {d.kind === "partners" ? (
          <Link className="btn" to="/partners" data-testid="import-to-partners">
            {M.toPartners}
          </Link>
        ) : (
          <Link className="btn" to="/invoices" data-testid="import-to-invoices">
            {M.toInvoices}
          </Link>
        )}
        {undoable ? (
          <button type="button" className="btn" onClick={() => setAsk(true)} data-testid="import-undo">
            {M.undo}
          </button>
        ) : null}
      </div>
      {ask ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && setAsk(false)}>
          <div className="dlg" role="dialog" aria-label={M.undo} onKeyDown={(e) => e.key === "Escape" && !busy && setAsk(false)}>
            <div className="dh">{M.undo}</div>
            <div className="db">
              <p>{d.kind === "partners" ? M.undoConfirmPartners : M.undoConfirm(d.result?.imported ?? 0)}</p>
            </div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus disabled={busy} onClick={() => setAsk(false)}>
                取消
              </button>
              <button type="button" className="btn shu" disabled={busy} onClick={() => void undo()} data-testid="import-undo-confirm">
                決定
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function UnpaidCells({ v }: { v: UnpaidRowValue }) {
  return (
    <>
      <td className="num">{v.managementNo}</td>
      <td>{v.clientName}</td>
      <td>{v.subject}</td>
      <td className="num">{v.issueDate ? md(v.issueDate) : ""}</td>
      <td className="num">{v.dueDate ? md(v.dueDate) : ""}</td>
      <td className="num">{v.total != null ? yen(v.total) : ""}</td>
    </>
  );
}

/** 取引先の行（#288）：名称・区分・顧客コード・仕入先コード・支店・取込（新規／更新（相手の名前）／n行目の支店） */
function PartnerCells({ r, typeLabel }: { r: ImportRowView; typeLabel: Record<PartnerRowValue["partnerType"], string> }) {
  const v = r.value as PartnerRowValue;
  const what = r.first != null ? M.partnerBranchOf(r.first) : r.match ? M.partnerUpdate(r.match.name) : M.partnerNew;
  return (
    <>
      <td>{v.name}</td>
      <td>{typeLabel[v.partnerType]}</td>
      <td className="code">{v.customerCode}</td>
      <td className="code">{v.vendorCode}</td>
      <td>{[v.branchName, v.branchCode].filter(Boolean).join(" ")}</td>
      <td data-testid={`import-row-what-${r.rowNo}`}>{what}</td>
    </>
  );
}
